import { useSelector } from 'react-redux';
import { selectAllowEmailBasedProjectCreation, selectFeatureFlags } from 'core/userContext';
import { UniqueIdentifierValues } from 'modules/projects/types';
import { Monospace2 } from 'ui/theme';

const EMAIL_OPTION = {
  name: () => <Monospace2>email</Monospace2>,
  id: UniqueIdentifierValues.email,
  description:
    'Each user must have a unique email address. Anonymous users without this field will be assigned a placeholder email.',
  value: UniqueIdentifierValues.email,
};

const USER_ID_OPTION = {
  name: () => <Monospace2>userId</Monospace2>,
  id: UniqueIdentifierValues.userId,
  description:
    'Each user must have a unique identifier which you assign. This allows for simpler management of anonymous users, and for multiple users who share the same email.',
  value: UniqueIdentifierValues.userId,
};

const HYBRID_OPTION = {
  name: () => (
    <>
      <p>
        Hybrid&nbsp; (<Monospace2>email</Monospace2>
        <span> and </span>
        <Monospace2>userId</Monospace2>)
      </p>
    </>
  ),
  id: UniqueIdentifierValues.emailOrUserId,
  description:
    'Each user may be identified by email or userId. At minimum, one of these fields must exist. Both email and userId fields must be unique—multiple users with the same email won’t be allowed.',
  value: UniqueIdentifierValues.emailOrUserId,
};

export const useUniqueIdentifierOptions = () => {
  const { EmailProjectTypeAvailable, EmailProjectTypePhaseout } = useSelector(selectFeatureFlags);
  const allowEmailBasedProjectCreation = useSelector(selectAllowEmailBasedProjectCreation);

  /**
   * allowEmailBasedProjectCreation reads on the BE from application config, and was added to differentiate
   * US deploys from EU deploys (in which email projects were disallowed from Email projects from the get-go).
   * EmailProjectTypeAvailable follows on that work to phase out organizations in US deployments as well --
   * it will be toggled off for all organizations except those that have been explicitly whitelisted.
   */

  if (EmailProjectTypePhaseout) {
    /**
     * EmailProjectTypePhaseout FF toggles on the changeset for disallowing email-based
     * project creation for all organizations except those that have been explicitly whitelisted.
     * It will be toggled on globally at a later date (6/30/24), after EmailProjectTypeAvailable
     * has been toggled on for all organizations that have created email-based projects within
     * the last year.
     */
    return allowEmailBasedProjectCreation && EmailProjectTypeAvailable
      ? [HYBRID_OPTION, USER_ID_OPTION, EMAIL_OPTION]
      : [HYBRID_OPTION, USER_ID_OPTION];
  } else {
    /** Current/legacy state provides email option if allowEmailBasedProjectCreation is true */
    return allowEmailBasedProjectCreation
      ? [EMAIL_OPTION, USER_ID_OPTION, HYBRID_OPTION]
      : [USER_ID_OPTION, HYBRID_OPTION];
  }
};
